import role19MfrsSchema from '../../../../assets/forms/role-610000-mfrs.form.json';
import role19MpersSchema from '../../../../assets/forms/role-610000-mpers.form.json';
import role8MfrsSchema from '../../../../assets/forms/role-210000-mfrs.form.json';
import role8MpersSchema from '../../../../assets/forms/role-210000-mpers.form.json';
import { DEI, DEI_EE_MFRS } from '../../../constants/qnames';
import { pickSchema, MPERS_ACCOUNTING_STANDARD } from './schema-picker.util';
import { pickRole12Schema } from './role12-statement-of-profit-or-loss.rules';
import { pickRole15Schema } from './role15-statement-of-comprehensive-income.rules';
import { referencedSheetKeysFromSchema } from '../cross-sheet-recompute.util';
import { isEmptyCell } from '../validation-field-key.util';
import {
  FilingSlices,
  resolveDimensionVisibility,
} from './dimension-visibility.util';
import { getDimensionVisibilityConfig } from './dimension-visibility-registry';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import { parseFlexibleDate } from '../../../utils/helpers';

/**
 * Shared business rules for Role 19 (Statement of Changes in Equity / SOCIE).
 *
 * Role 19 is a Tier 4 cross-sheet cycle-pair sheet:
 *
 *   - **Role 8 (SFP) ↔ Role 19 (SOCIE)** — symmetric `:Equity` vs `:Equity:End`
 *     reconciliation, gated by consecutive financial-year dates. The
 *     `crossSheetEquityByYear` rule lives on the SOCIE schema's `:Equity:End`
 *     control (role-610000-* form.json) — see
 *     `EquityReconciliationService.validateCrossSheetEquityByYear`. Role 8's
 *     validator probes the same rule; this file exposes the rule-resolution
 *     primitives both sides share.
 *
 *   - **Role 15 (SOCI) → Role 19 (SOCIE)** — one-way `ComprehensiveIncome` /
 *     `OtherComprehensiveIncome` equality (schema `validation.equality`
 *     blocks). Role 15's validator is the complementary (reverse-direction)
 *     check: the two sheets' totals must agree column-by-column across the
 *     four (dimension × period) tables.
 *
 *   - **Role 12 (SOPL) → Role 19 (SOCIE)** — one-way `ProfitLoss` equality
 *     (schema `validation.equality`). The Role-12 → Role-19 edge is
 *     declared in `SheetDependencyService.DEPENDENCIES`, so Role 12 edits
 *     invalidate Role 19's cached status.
 *
 * Plus two SOCIE-only self-reconciliations on the `ifrs-*:Equity:Beginning`
 * control:
 *
 *   - **SOCIE roll-forward — per equity component** (`consecutiveYearEquity`):
 *     for each non-total equity column (Issued capital, Retained earnings,
 *     Treasury shares, individual reserves, etc.) the previous-year `End`
 *     cell must equal the current-year `Beginning` cell, column by column.
 *     Fires only when the financial-year boundary is consecutive.
 *
 *   - **SOCIE roll-forward — total column** (`consecutiveYearEquityTotal`):
 *     the same `End` vs `Beginning` check restricted to the Total column
 *     (`ifrs-full:EquityMember` / `ifrs-smes:EquityMember`).
 *
 * Both the editor's role19-610000-statement-of-changes-in-equity.component.ts
 * AND role19StatementOfChangesInEquityValidator import from this file so the
 * schema selection, cross-sheet target tables, and roll-forward primitives
 * live in exactly one place.
 *
 * ---- SOCIE "roll-forward" in one sentence ----------------------------------
 * The component models roll-forward as three complementary sums rather than a
 * single opening + movements = closing equation:
 *   1. `EquityBalanceRestated = Equity:Beginning + ImpactOfChangeInAccountingStandards` (calculated).
 *   2. `ChangesInEquity = ComprehensiveIncome + (all movement rows) - DividendsPaid` (calculated).
 *   3. `Equity:End = EquityBalanceRestated + ChangesInEquity` (calculated).
 * The consecutiveYearEquity / consecutiveYearEquityTotal checks then bind
 * period N's End to period N+1's Beginning, closing the loop across years.
 * The validator reads the post-calculation persisted values — same posture as
 * Tier 4 Batch 1 (role 8 / role 12) — so it does NOT replay the arithmetic.
 * If calculated cells are missing we fall back to 0 (matches the editor's
 * "calculated fields display 0 when null" behaviour).
 *
 * ---- Cross-sheet dependency surface --------------------------------------
 *
 * Role 19 reads four cross-sheet slices for its OWN validation:
 *   1. `filingPrerequisites` (Role 0) — drives column visibility (Consolidated
 *      vs Separate, CurrentYear vs PreviousYear) AND the consecutive-year
 *      date gate (`CompanyCurrentFinancialYearStartDate` /
 *      `CompanyPreviousFinancialYearEndDate`).
 *   2. `statementOfProfitOrLoss` (Role 12) — `ProfitLoss` equality source.
 *   3. `statementOfComprehensiveIncome` (Role 15) — `OtherComprehensiveIncome`
 *      / `ComprehensiveIncome` equality sources.
 *   4. `statementOfFinancialPosition` (Role 8) — `Equity` total for the
 *      `crossSheetEquityByYear` rule (SOCIE `Equity:End` ↔ SFP `Equity`).
 */

// ---------------------------------------------------------------------------
// Accounting-standard selection (mirrors role 8 / 15 / 20 literals so the
// cross-sheet lift family stays consistent).
// ---------------------------------------------------------------------------

export const ROLE19_MPERS_ACCOUNTING_STANDARD = MPERS_ACCOUNTING_STANDARD;

export const ROLE19_MFRS_SCHEMA_FILE = 'role-610000-mfrs.form.json';
export const ROLE19_MPERS_SCHEMA_FILE = 'role-610000-mpers.form.json';

/**
 * Resolve the correct role 19 schema filename for the given accounting
 * standard. Mirrors the component's `loadSchema()` ternary.
 */
export function pickRole19SchemaFile(
  accountingStandard: string | undefined | null
): string {
  return pickSchema(
    accountingStandard,
    ROLE19_MFRS_SCHEMA_FILE,
    ROLE19_MPERS_SCHEMA_FILE
  );
}

/**
 * Resolve the role 19 schema object. Used by the pure validator which cannot
 * fetch() at validate-time (the listing/dashboard's cache-miss path runs
 * synchronously) so both variants are imported eagerly and selected from
 * memory.
 */
export function pickRole19Schema(
  accountingStandard: string | undefined | null
): Role19Schema {
  return pickSchema(
    accountingStandard,
    role19MfrsSchema as unknown as Role19Schema,
    role19MpersSchema as unknown as Role19Schema
  );
}

/**
 * Resolve the role 8 schema object. The `crossSheetEquityByYear` rule's
 * compareField qname is declared on the SOCIE control but we ALSO need the
 * SFP `Equity` control to decide the calculated-or-readonly fallback for a
 * missing cell (matches EquityReconciliationService.validateCrossSheetEquityByYear).
 */
export function pickRole19Role8Schema(
  accountingStandard: string | undefined | null
): Role19Schema {
  return pickSchema(
    accountingStandard,
    role8MfrsSchema as unknown as Role19Schema,
    role8MpersSchema as unknown as Role19Schema
  );
}

/**
 * The sheets role 19 must recompute BEFORE any cross-sheet compare — the single
 * roster shared by the pure validator and the mounted editor.
 *
 * Two sources, and the second is the one that is easy to lose:
 *   - `validation.equality` rules on the SOCIE schema (roles 12 and 15), which
 *     `referencedSheetKeysFromSchema` derives.
 *   - Role 8 (SFP), reached by the `crossSheetEquityByYear` rule, which is NOT a
 *     `validation.equality` rule and so is invisible to that helper. It has to be
 *     added explicitly.
 *
 * The mounted editor used to derive its own roster from the schema alone and so
 * omitted role 8 — see `role19SchemaForReferencedSheet` for what that cost.
 */
export function role19ReferencedSheetKeys(schema: Role19Schema): string[] {
  return Array.from(
    new Set<string>([
      ...referencedSheetKeysFromSchema(schema, SHEET_KEYS.statementOfChangesInEquity),
      SHEET_KEYS.statementOfFinancialPosition,
    ])
  );
}

/**
 * Schema provider for `recomputeReferencedSheets` — the single source shared by
 * role 19's pure validator and its mounted editor.
 *
 * `resolveInheritedLeaves` is GATED on this returning `controls`: no schema means
 * the referenced sheet's own leaf inheritance is silently SKIPPED and it
 * recomputes from its raw slice. So every inheriting sheet role 19 can reference
 * needs a branch here — role 8 ⇐ role 9, role 12 ⇐ role 13, role 15 ⇐ role 12.
 *
 * Why this is shared rather than duplicated (both halves were wrong, differently):
 *
 *   - The MOUNTED editor passed `ValidationContextService.getCachedSchema`, whose
 *     cache is populated by `preloadReferencedSchemas` — which walks
 *     `validation.equality` rules ONLY. Role 8 is reached by `crossSheetEquityByYear`,
 *     so its schema was never cached and its role-9 inheritance never replayed. The
 *     editor therefore compared SOCIE's `Equity at end of period` against role 8's
 *     STALE persisted `Equity` and showed a mismatch error the sheet verdict did not
 *     agree with — a red message under a `Success *` chip. Opening SOFP mounted role
 *     8, which pulled role 9 and persisted the corrected total, so returning to SOCIE
 *     cleared the message. Reported 2026-09-21 on an MFRS filing: role 9 carried
 *     `OtherComponentsOfEquity` 12.15 where role 8 still held 12.14, putting role 8's
 *     persisted `Equity` at 4150.90 against SOCIE's 4150.92.
 *   - The PURE validator had role 8 and role 12 but not role 15, so role 15 kept its
 *     stale extracted `Profit (loss)` — the mirror-image defect on the same seam.
 *
 * Keyed on taxonomy rather than the accounting-standard literal because both call
 * sites already hold the taxonomy; `pickSchema` is MFRS-default, so the mapping
 * back to a literal is exact.
 */
export function role19SchemaForReferencedSheet(
  sheetKey: string,
  taxonomy: 'MFRS' | 'MPERS'
): { controls?: any[] } | undefined {
  const accountingStandard =
    taxonomy === 'MPERS' ? ROLE19_MPERS_ACCOUNTING_STANDARD : undefined;
  if (sheetKey === SHEET_KEYS.statementOfFinancialPosition) {
    return pickRole19Role8Schema(accountingStandard);
  }
  if (sheetKey === SHEET_KEYS.statementOfProfitOrLoss) {
    return pickRole12Schema(accountingStandard);
  }
  if (sheetKey === SHEET_KEYS.statementOfComprehensiveIncome) {
    return pickRole15Schema(accountingStandard) as { controls?: any[] };
  }
  return undefined;
}

/** Returns true when the accounting standard resolves to MFRS. */
export function isRole19MFRS(
  accountingStandard: string | undefined | null
): boolean {
  return accountingStandard !== ROLE19_MPERS_ACCOUNTING_STANDARD;
}

/** Map the accounting-standard literal to the schema taxonomy tag. */
export function role19AccountingStandardToTaxonomy(
  accountingStandard: string | undefined | null
): 'MFRS' | 'MPERS' {
  return accountingStandard === ROLE19_MPERS_ACCOUNTING_STANDARD
    ? 'MPERS'
    : 'MFRS';
}

/** Extract MFRS|MPERS from a schema's `taxonomy` field (e.g. "FS-MFRS"). */
export function extractRole19SchemaTaxonomy(
  schema: Role19Schema
): 'MFRS' | 'MPERS' | null {
  const taxonomy = schema.taxonomy;
  if (!taxonomy) return null;
  if (taxonomy.includes('MFRS')) return 'MFRS';
  if (taxonomy.includes('MPERS')) return 'MPERS';
  return null;
}

// ---------------------------------------------------------------------------
// Column-visibility gating literals.
//
// As of the dimension-visibility standardization, the AUTHORITATIVE gate
// descriptors live in `dimension-visibility-registry.ts` (NATURE / PREP), and
// `resolveRole19ColumnVisibility` below delegates to the shared resolver. These
// four constants are retained because the role-19 validator spec imports them
// to build fixtures and pin scenarios; keep them in sync with the registry's
// NATURE / PREP descriptors if the DEI field names ever change.
// ---------------------------------------------------------------------------

export const ROLE19_NATURE_OF_FINANCIAL_STATEMENTS_FIELD =
  DEI_EE_MFRS.NatureOfFinancialStatements;
export const ROLE19_NATURE_CONSOLIDATED_VALUE = 'Consolidated';

export const ROLE19_PREPARATION_TYPE_FIELD =
  DEI.DisclosureOfFinancialStatementsPreparationForCurrentSubmission;
export const ROLE19_PREPARATION_FIRST_TIME_VALUE =
  'First time preparation of financial statements after incorporation';

export const ROLE19_CURRENT_YEAR_START_FIELD =
  DEI.CompanyCurrentFinancialYearStartDate;
export const ROLE19_PREVIOUS_YEAR_END_FIELD =
  DEI.CompanyPreviousFinancialYearEndDate;

export type Role19ConsolidationType = 'Consolidated' | 'Separate';
/** SOCIE uses 'Current' / 'Previous' (no 'Year' suffix) for its permutation keys. */
export type Role19PeriodType = 'Current' | 'Previous';

/** Human-readable column suffix used in SOCIE `{qname}:{column}[C][P]` keys. */
export type Role19PeriodSuffix = 'CurrentYear' | 'PreviousYear';

// ---------------------------------------------------------------------------
// Schema shapes (narrow — only what the validator reads).
// ---------------------------------------------------------------------------

export interface Role19EqualityRule {
  sheet: string;
  field: string;
  operation?: 'sum' | string;
  message?: string;
  tolerance?: number;
  taxonomy?: string;
  skipIfSourceNull?: boolean;
  skipIfTargetNull?: boolean;
}

export interface Role19ConsecutiveYearEquityRule {
  compareField: string;
  sheet: string;
  tolerance?: number;
  taxonomy?: string;
  message?: string;
}

export interface Role19ColumnSumValidation {
  subTotalColumnQname: string;
  componentColumnQnames: string[];
  subTotalLabel: string;
  componentLabels: string[];
  /**
   * Optional allow-list of row qnames this sub-total check applies to. When
   * present and non-empty, the reconciliation runs ONLY for these rows; when
   * absent, it runs for every non-header `equityColumns` row (legacy default).
   * Lets a schema scope a sub-total to specific equity-movement rows — e.g. the
   * non-distributable reserves sub-total excludes the Acquisition-of-subsidiary
   * row (both taxonomies) and the Total-increase-in-equity row (MPERS only).
   */
  appliesToRowQnames?: string[];
}

export interface Role19EquityColumn {
  qname: string;
  label: string;
  order: number;
  calculated?: boolean;
}

export interface Role19SchemaControl {
  qname: string;
  label?: string;
  type?: string;
  required?: boolean;
  ui?: {
    controlType?: string;
    control?: string;
    level?: number;
    dualColumn?: boolean;
    equityColumns?: boolean;
    calculated?: boolean;
    readonly?: boolean;
    required?: boolean;
  };
  validation?: {
    dataType?: string;
    equality?: Role19EqualityRule[];
    consecutiveYearEquity?: Role19ConsecutiveYearEquityRule;
    consecutiveYearEquityTotal?: Role19ConsecutiveYearEquityRule;
    crossSheetEquityByYear?: {
      compareField: string;
      sheet: string;
      tolerance?: number;
      taxonomy?: string;
    };
  };
}

export interface Role19Schema {
  role?: string;
  roleId?: string;
  taxonomy?: string;
  sheetId?: string;
  label?: string;
  columnSumValidations?: Role19ColumnSumValidation[];
  equityColumns?: Role19EquityColumn[];
  controls: Role19SchemaControl[];
}

// ---------------------------------------------------------------------------
// Column-visibility + date gating.
// ---------------------------------------------------------------------------

export interface Role19ColumnVisibility {
  consolidationType: Role19ConsolidationType;
  periodType: Role19PeriodType;
  periodSuffix: Role19PeriodSuffix;
  /** Human label used in error-message filtering: "Consolidated Current" etc. */
  label: string;
  visible: boolean;
}

/**
 * SOCIE column visibility, in the richer role-19 cell shape the validator's
 * key-builders and roll-forward loops consume (`consolidationType` for the
 * dimension, `periodSuffix` for the `[CurrentYear|PreviousYear]` key segment,
 * `periodType` for the `Current`/`Previous` loop comparisons, `label` for
 * error-message filtering).
 *
 * This is now a THIN ADAPTER over the shared, config-driven resolver
 * (`resolveDimensionVisibility` + the central `dimension-visibility-registry`)
 * — the SAME resolver the mounted editor calls through
 * `CrossReferenceService.getVisibility()`, so the on-screen columns and the
 * validated columns cannot drift. The shared cell only carries
 * `{ dimension, period, visible }`; we map it back into the role-19 shape here:
 *   - `dimension`  → `consolidationType` (identical 'Consolidated'/'Separate')
 *   - `period`     → `periodSuffix`      (identical 'CurrentYear'/'PreviousYear')
 *   - derived      → `periodType`        ('CurrentYear' → 'Current', else 'Previous')
 *   - derived      → `label`             ('<Consolidated|Separate> <Current|Previous>')
 *
 * Resolution rule is the shared prereq-first-then-filingInfo posture. The
 * approved default change rides along: an unknown/unset Nature now defaults the
 * Consolidated dimension to SHOWN (matches the shared resolver's
 * pre-subscription posture), where the old role-local body defaulted it to
 * shown too for unknown nature — so visibility is preserved.
 *
 * Accepts either a bare filingPrerequisites record (legacy single-arg call,
 * used by tests) or an explicit {@link FilingSlices} pair so the pure validator
 * can pass `filingInfo` as the first-listing-visit fallback (mirror of the role
 * 12 lift). When given a bare record it is treated as the filingPrerequisites
 * slice.
 */
export function resolveRole19ColumnVisibility(
  slices: FilingSlices | Record<string, unknown> | undefined
): ReadonlyArray<Role19ColumnVisibility> {
  const filingSlices: FilingSlices =
    slices && ('filingPrerequisites' in slices || 'filingInfo' in slices)
      ? (slices as FilingSlices)
      : { filingPrerequisites: slices as Record<string, unknown> | undefined };

  const cells = resolveDimensionVisibility(
    getDimensionVisibilityConfig(SHEET_KEYS.statementOfChangesInEquity)!,
    filingSlices
  );

  return cells.map((cell) => {
    const periodType: Role19PeriodType =
      cell.period === 'CurrentYear' ? 'Current' : 'Previous';
    return {
      consolidationType: cell.dimension,
      periodType,
      periodSuffix: cell.period,
      label: `${cell.dimension} ${periodType}`,
      visible: cell.visible,
    };
  });
}

/** Mirrors the validateCrossSheetEquityByYear / consecutiveYearEquity* gate. */
export function areRole19YearsConsecutive(
  filingPrerequisites: Record<string, unknown> | undefined
): boolean {
  const startStr = filingPrerequisites?.[ROLE19_CURRENT_YEAR_START_FIELD] as
    | string
    | undefined;
  const endStr = filingPrerequisites?.[ROLE19_PREVIOUS_YEAR_END_FIELD] as
    | string
    | undefined;
  if (!startStr || !endStr) return false;

  // parseFlexibleDate — DEI dates are DD/MM/YYYY; native new Date() misreads
  // slash-dates as US MM/DD/YYYY → Invalid Date → false "not consecutive".
  const startDate = parseFlexibleDate(startStr);
  const endDate = parseFlexibleDate(endStr);
  if (!startDate || !endDate) {
    return false;
  }

  const dayAfterEnd = new Date(endDate);
  dayAfterEnd.setDate(dayAfterEnd.getDate() + 1);
  return startDate.getTime() === dayAfterEnd.getTime();
}

// ---------------------------------------------------------------------------
// Key-builder primitives.
// ---------------------------------------------------------------------------

/**
 * SOCIE equity-column key: `{rowQname}:{columnQname}[{Cons}][{CurrentYear|PreviousYear}]`.
 * Mirrors `getEquityColumnControlName(rowQname, columnQname, consolidationType, periodType)`.
 */
export function buildRole19EquityColumnKey(
  rowQname: string,
  columnQname: string,
  consolidationType: Role19ConsolidationType,
  periodSuffix: Role19PeriodSuffix
): string {
  return `${rowQname}:${columnQname}[${consolidationType}][${periodSuffix}]`;
}

/**
 * SFP (Role 8) dualColumn key for cross-sheet equality:
 * `{qname}[{Cons}][{CurrentYear|PreviousYear}]`.
 */
export function buildRole19SfpKey(
  qname: string,
  consolidationType: Role19ConsolidationType,
  periodSuffix: Role19PeriodSuffix
): string {
  return `${qname}[${consolidationType}][${periodSuffix}]`;
}

/**
 * Pick the equity-Total-column qname for the given accounting standard.
 * Mirrors the `totalColumnQname` ternary used in every calculation /
 * validation path in the component.
 */
export function getRole19TotalColumnQname(
  accountingStandard: string | undefined | null
): string {
  return isRole19MFRS(accountingStandard)
    ? 'ifrs-full:EquityMember'
    : 'ifrs-smes:EquityMember';
}

/**
 * Pick the SFP `Equity` and SOCIE `Equity:End` qnames used by
 * `crossSheetEquityByYear`. Mirrors EquityReconciliationService's taxonomy
 * ternary.
 */
export interface Role19EquityTargets {
  sfpEquityQname: string;
  socieEquityEndQname: string;
}

export function getRole19EquityCrossSheetTargets(
  accountingStandard: string | undefined | null
): Role19EquityTargets {
  const mfrs = isRole19MFRS(accountingStandard);
  return {
    sfpEquityQname: mfrs ? 'ifrs-full:Equity' : 'ifrs-smes:Equity',
    socieEquityEndQname: mfrs ? 'ifrs-full:Equity:End' : 'ifrs-smes:Equity:End',
  };
}

// ---------------------------------------------------------------------------
// Empty / header / calculated detection.
// ---------------------------------------------------------------------------

/**
 * Empty-cell check. Mirrors the component's `isRequiredFieldEmpty()`
 * (null / undefined / ''). Zero is a valid monetary value.
 */
export function isRole19Empty(value: unknown): boolean {
  return isEmptyCell(value);
}

/** Header detection — mirrors the component's `isSectionHeader()`. */
export function isRole19SectionHeader(control: Role19SchemaControl): boolean {
  return (
    control.type === 'SectionHeader' ||
    control.type === 'TextBlock' ||
    control.ui?.controlType === 'section-header'
  );
}

/** Mirrors the component's `isCalculatedField()` / validation-service probe. */
export function isRole19CalculatedField(
  control: Role19SchemaControl | undefined
): boolean {
  return control?.ui?.calculated === true || control?.ui?.readonly === true;
}

/**
 * Schema-declared static required-field check with the LB-001 fix-guard.
 *
 * The editor's `isRequiredField(control)` reads ONLY `control.ui.required`,
 * while the schema also carries a top-level `control.required` flag (both
 * schemas set both flags on the same fields today). This helper accepts
 * either source so a future schema edit that flips only one flag is still
 * picked up. Section headers, calculated / readonly rows, and the handful of
 * non-monetary rows are skipped.
 */
export function isRole19StaticallyRequired(
  control: Role19SchemaControl
): boolean {
  if (isRole19SectionHeader(control)) return false;
  if (isRole19CalculatedField(control)) return false;
  // Only enforce requiredness on monetary item rows — other row types don't
  // carry required FormControls in buildForm().
  if (control.type !== 'MonetaryItemType') return false;

  if (control.required === true) return true;
  if (control.ui?.required === true) return true;
  return false;
}

// ---------------------------------------------------------------------------
// Equity-total horizontal sum (MFRS Consolidated only).
// ---------------------------------------------------------------------------

/**
 * Component / component-label pairing for the MFRS Consolidated-only
 * `EquityMember = EquityAttributableToOwnersOfParentMember +
 *                 OtherComponentsOfEquityMember +
 *                 NoncontrollingInterestsMember`
 * horizontal check. Mirrors the literals in `validateEquityTotal()`.
 */
export const ROLE19_EQUITY_TOTAL_COMPONENT_COLUMNS = [
  'ifrs-full:EquityAttributableToOwnersOfParentMember',
  'ifrs-full:OtherComponentsOfEquityMember',
  'ifrs-full:NoncontrollingInterestsMember',
] as const;

/**
 * Row deliberately excluded from the MFRS equity-total horizontal sum check.
 * Mirrors the `excludedRowQname` literal in the component.
 */
export const ROLE19_EQUITY_TOTAL_EXCLUDED_ROW =
  'ifrs-full:IncreaseDecreaseThroughAcquisitionOfSubsidiary';
